import { Box, Card, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import StackedBarChart from '@components/Charts/Bar/StackedBarChart';
import Icon from '@components/Icon/Icon';
import SkeletonLoader from '@components/SkeletonLoader';

import {
  BLOOD_PRESSURE,
  BODY_HEIGHT,
  getObservationConfig,
} from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';
import { useHealthChart } from '@hooks/useHealthChart';
import { useIsMobile } from '@hooks/useIsMobile';
import { useMetricWithinRange } from '@hooks/useMetricWithinRange';
import useReferenceRange from '@hooks/useReferenceRange';

import { getUnit } from '@utils/conversion';
import { formatVitalSign } from '@utils/format';

import { ButtonParameterDialog } from '../Range/Dialog';
import type { Kpi } from './index';

interface KpiCardProps {
  /** KPI descriptor, value, data, and components */
  kpi: Kpi;
}

export default function KpiCard({ kpi }: KpiCardProps) {
  const { components = [], descriptor, value } = kpi;

  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const {
    state: { conversion },
  } = useConversion();
  const {
    palette: { text },
  } = useTheme();
  const { code, loading, normalRange } = useHealthChart();
  const { parameterMap } = useReferenceRange();
  const { determineMetricRange } = useMetricWithinRange();

  const signType = getObservationConfig(code);
  const format = signType?.format;
  const unit = signType?.unit;
  const convertedUnit = getUnit(unit, conversion[code] ? 'CON' : 'SI', code);

  const KPI_SIZE =
    isMobile || code === BLOOD_PRESSURE || code === BODY_HEIGHT ? 'h6' : 'h5';
  const isBloodPressure = code === BLOOD_PRESSURE;

  const displayFormattedValue = ({
    val,
    idx,
    type,
  }: {
    val: number;
    idx: number;
    type: string;
  }) => {
    const obsValue = kpi.valueInRange?.at(idx) ?? val;
    const range = determineMetricRange(obsValue, type);
    const isChange = descriptor.id === 'healthTracker.detail.kpi.change';

    return (
      <span
        key={`${descriptor.id}-${idx}-${val}`}
        style={{
          color: range ? parameterMap[range].colour : text.primary,
          fontSize: 20,
        }}
      >
        {isChange && (
          <Icon
            name={val > 0 ? 'arrow-up' : 'arrow-down'}
            size="xs"
            type="fas"
          />
        )}
        {formatVitalSign(
          {
            // The arrow carries the direction, so show magnitude only - a
            // signed value would read as "↓ -0' 1"".
            value: isChange ? Math.abs(val) : val,
            unit: undefined,
          },
          false,
          format ? (value) => format(value, convertedUnit) : undefined
        )}

        {idx !== value.length - 1 &&
          code === BLOOD_PRESSURE &&
          (descriptor.id === 'healthTracker.detail.kpi.start' ||
            descriptor.id === 'healthTracker.detail.kpi.end') && (
            <span style={{ color: text.secondary }}>/&nbsp;</span>
          )}
      </span>
    );
  };

  const component =
    components.length > 0 ? (
      <Stack>
        {components.map((comp, compIndex) => {
          return (
            <Stack
              alignItems="center"
              direction="row"
              key={`comp-${comp.value}-${compIndex + 1}`}
              spacing={2}
            >
              <Typography sx={{ lineHeight: '100%' }} variant="overline">
                {formatMessage(comp.descriptor)}
              </Typography>

              {comp.value.map((val, idx) => {
                return displayFormattedValue({
                  val,
                  idx,
                  type: compIndex === 0 ? 'systolic' : 'diastolic',
                });
              })}
            </Stack>
          );
        })}
      </Stack>
    ) : null;

  return (
    <SkeletonLoader height={111} loading={loading}>
      <Card
        sx={({ palette: { grey } }) => ({
          height: 111,
          border: `1px solid ${grey[200]}`,
          textAlign: 'center',
          alignContent: 'center',
          backgroundColor: grey.A100,
        })}
      >
        {descriptor.id === 'healthTracker.detail.kpi.percentage' &&
        normalRange ? (
          <ButtonParameterDialog />
        ) : (
          <Typography variant="overline" color="text.secondary">
            {formatMessage(descriptor)}
          </Typography>
        )}

        {descriptor.id === 'healthTracker.detail.kpi.percentage' && kpi.data ? (
          <Box px={1.5}>
            <StackedBarChart chartData={kpi.data} />
          </Box>
        ) : (
          <Typography
            mx={1}
            variant={KPI_SIZE}
            color="text.secondary"
            fontWeight="600"
            sx={{
              display: 'flex',
              justifyContent:
                descriptor.id === 'healthTracker.detail.kpi.start' ||
                descriptor.id === 'healthTracker.detail.kpi.end'
                  ? 'center'
                  : 'space-evenly',
            }}
          >
            {component ??
              (value.length
                ? value.map((val, valIndex) =>
                    displayFormattedValue({
                      val,
                      idx: valIndex,
                      type: isBloodPressure
                        ? valIndex === 0
                          ? 'systolic'
                          : valIndex === 1
                            ? 'diastolic'
                            : code
                        : code,
                    })
                  )
                : '- -')}
          </Typography>
        )}
      </Card>
    </SkeletonLoader>
  );
}
